/**
 * Resource Service
 * Handles equipment, labor, materials, and resource allocation API calls
 */

import { httpClient, ApiResponse } from './http.client';

// Resource Types
export type ResourceType = 'Labor' | 'Equipment' | 'Material' | 'Subcontractor';
export type EquipmentStatus = 'Available' | 'In Use' | 'Maintenance' | 'Out of Service';
export type SkillLevel = 'Entry Level' | 'Intermediate' | 'Skilled' | 'Expert';

// Resource Interfaces
export interface Resource {
  resource_id: string;
  organization_id: string;
  resource_name: string;
  resource_type: ResourceType;
  unit_cost_rate?: number;
  unit_of_measure?: string;
  description?: string;
  skill_level?: string;
  availability?: string;
  supplier_contact?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Equipment {
  equipment_id: string;
  organization_id: string;
  equipment_name: string;
  equipment_type: string;
  model?: string;
  manufacturer?: string;
  serial_number?: string;
  hourly_rate?: number;
  daily_rate?: number;
  status: EquipmentStatus;
  created_at: string;
  updated_at: string;
}

export interface LaborCategory {
  labor_category_id: string;
  organization_id: string;
  category_name: string;
  description?: string;
  skill_level: SkillLevel;
  hourly_rate?: number;
  overtime_rate?: number;
  created_at: string;
  updated_at: string;
}

export interface MaterialCategory {
  material_category_id: string;
  organization_id: string;
  category_name: string;
  description?: string;
  category_type: string;
  unit_of_measure: string;
  standard_rate?: number;
  created_at: string;
  updated_at: string;
}

export interface ResourceAllocation {
  allocation_id: string;
  organization_id: string;
  project_id: string;
  task_id?: string;
  resource_type: 'Equipment' | 'Labor' | 'Material';
  equipment_id?: string;
  labor_category_id?: string;
  material_category_id?: string;
  allocation_date: string;
  quantity_allocated: number;
  unit_of_measure: string;
  allocation_notes?: string;
  allocated_by_user_id: string;
  created_at: string;
  updated_at: string;
}

export interface TaskResource {
  task_resource_id: string;
  organization_id: string;
  task_id: string;
  resource_id: string;
  quantity_required: number;
  unit_of_measure?: string;
  start_date_planned?: string;
  end_date_planned?: string;
  start_date_actual?: string;
  end_date_actual?: string;
  planned_cost?: number;
  actual_cost?: number;
  allocation_status: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

class ResourceService {
  /**
   * Get all resources for organization
   */
  async getResources(
    organizationId: string,
    page = 1,
    perPage = 100,
    filters?: { resource_type?: ResourceType; is_active?: boolean }
  ): Promise<ApiResponse<{ data: Resource[]; total: number }>> {
    const params: any = {
      organization_id: organizationId,
      page: page.toString(),
      per_page: perPage.toString(),
      ...filters
    };

    const queryParams = new URLSearchParams(params);
    return httpClient.get<{ data: Resource[]; total: number }>(
      `/api/v1/resources/?${queryParams}`
    );
  }

  /**
   * Create a new resource
   */
  async createResource(resourceData: {
    organization_id: string;
    resource_name: string;
    resource_type: ResourceType;
    unit_cost_rate?: number;
    unit_of_measure?: string;
    description?: string;
    skill_level?: string;
    availability?: string;
    supplier_contact?: string;
  }): Promise<ApiResponse<Resource>> {
    return httpClient.post<Resource>('/api/v1/resources/', resourceData);
  }

  /**
   * Get unified resource ID from category-specific resource
   * This maps category-specific IDs (equipment_id, labor_category_id, material_category_id)
   * to the unified resources table resource_id
   */
  async getUnifiedResourceId(
    categoryId: string,
    categoryType: 'equipment' | 'labor' | 'material',
    organizationId: string
  ): Promise<string | null> {
    try {
      // Query the unified resources table with the category-specific ID
      // For now, we'll just return the categoryId as-is since the backend
      // should handle the mapping. If this causes issues, we may need to
      // add a backend endpoint to retrieve the unified resource_id
      return categoryId;
    } catch (error) {
      console.error('Failed to get unified resource ID:', error);
      return null;
    }
  }

  /**
   * Get all equipment
   */
  async getEquipment(
    organizationId: string,
    page = 1,
    perPage = 100
  ): Promise<ApiResponse<{ data: Equipment[]; total: number }>> {
    const params = {
      organization_id: organizationId,
      page: page.toString(),
      per_page: perPage.toString()
    };

    const queryParams = new URLSearchParams(params);
    return httpClient.get<{ data: Equipment[]; total: number }>(
      `/api/v1/resources/equipment/?${queryParams}`
    );
  }

  /**
   * Create equipment
   */
  async createEquipment(equipmentData: {
    organization_id: string;
    equipment_name: string;
    equipment_type: string;
    model?: string;
    manufacturer?: string;
    serial_number?: string;
    hourly_rate?: number;
    daily_rate?: number;
    status?: EquipmentStatus;
  }): Promise<ApiResponse<Equipment>> {
    return httpClient.post<Equipment>('/api/v1/resources/equipment/', equipmentData);
  }

  /**
   * Get all labor categories
   */
  async getLaborCategories(
    organizationId: string,
    page = 1,
    perPage = 100
  ): Promise<ApiResponse<{ data: LaborCategory[]; total: number }>> {
    const params = {
      organization_id: organizationId,
      page: page.toString(),
      per_page: perPage.toString()
    };

    const queryParams = new URLSearchParams(params);
    return httpClient.get<{ data: LaborCategory[]; total: number }>(
      `/api/v1/resources/labor-categories/?${queryParams}`
    );
  }

  /**
   * Create labor category
   */
  async createLaborCategory(laborData: {
    organization_id: string;
    category_name: string;
    description?: string;
    skill_level: SkillLevel;
    hourly_rate?: number;
    overtime_rate?: number;
  }): Promise<ApiResponse<LaborCategory>> {
    return httpClient.post<LaborCategory>('/api/v1/resources/labor-categories/', laborData);
  }

  /**
   * Get all material categories
   */
  async getMaterialCategories(
    organizationId: string,
    page = 1,
    perPage = 100
  ): Promise<ApiResponse<{ data: MaterialCategory[]; total: number }>> {
    const params = {
      organization_id: organizationId,
      page: page.toString(),
      per_page: perPage.toString()
    };

    const queryParams = new URLSearchParams(params);
    return httpClient.get<{ data: MaterialCategory[]; total: number }>(
      `/api/v1/resources/material-categories/?${queryParams}`
    );
  }

  /**
   * Create material category
   */
  async createMaterialCategory(materialData: {
    organization_id: string;
    category_name: string;
    description?: string;
    category_type: string;
    unit_of_measure: string;
    standard_rate?: number;
  }): Promise<ApiResponse<MaterialCategory>> {
    return httpClient.post<MaterialCategory>('/api/v1/resources/material-categories/', materialData);
  }

  /**
   * Create resource allocation
   */
  async createResourceAllocation(allocationData: {
    organization_id: string;
    project_id: string;
    task_id?: string;
    resource_type: 'Equipment' | 'Labor' | 'Material';
    equipment_id?: string;
    labor_category_id?: string;
    material_category_id?: string;
    allocation_date: string;
    quantity_allocated: number;
    unit_of_measure: string;
    allocation_notes?: string;
  }): Promise<ApiResponse<ResourceAllocation>> {
    return httpClient.post<ResourceAllocation>('/api/v1/resources/allocations/', allocationData);
  }

  /**
   * Get resource allocations for a task
   */
  async getTaskResourceAllocations(
    taskId: string,
    organizationId: string
  ): Promise<ApiResponse<ResourceAllocation[]>> {
    const params = {
      task_id: taskId,
      organization_id: organizationId
    };

    const queryParams = new URLSearchParams(params);
    return httpClient.get<ResourceAllocation[]>(
      `/api/v1/resources/allocations/?${queryParams}`
    );
  }

  /**
   * Get task resources (planning.task_resources table)
   */
  async getTaskResources(
    taskId: string,
    organizationId: string
  ): Promise<ApiResponse<TaskResource[]>> {
    return httpClient.get<TaskResource[]>(
      `/api/v1/tasks/${taskId}/resources?organization_id=${organizationId}`
    );
  }

  /**
   * Add resource to task via resource allocations endpoint
   */
  async addTaskResource(taskId: string, resourceData: {
    organization_id: string;
    resource_id: string;
    quantity_required: number;
    unit_of_measure?: string;
    start_date_planned?: string;
    end_date_planned?: string;
    planned_cost?: number;
    allocation_status?: string;
    notes?: string;
  }): Promise<ApiResponse<TaskResource>> {
    return httpClient.post<TaskResource>(
      '/api/v1/resources/allocations',
      {
        ...resourceData,
        task_id: taskId
      }
    );
  }

  /**
   * Update task resource
   */
  async updateTaskResource(
    taskResourceId: string,
    updates: {
      quantity_required?: number;
      unit_of_measure?: string;
      start_date_planned?: string;
      end_date_planned?: string;
      start_date_actual?: string;
      end_date_actual?: string;
      planned_cost?: number;
      actual_cost?: number;
      allocation_status?: string;
      notes?: string;
    }
  ): Promise<ApiResponse<TaskResource>> {
    return httpClient.put<TaskResource>(
      `/api/v1/tasks/resources/${taskResourceId}`,
      updates
    );
  }

  /**
   * Delete task resource
   */
  async deleteTaskResource(taskResourceId: string): Promise<ApiResponse<void>> {
    return httpClient.delete<void>(`/api/v1/tasks/resources/${taskResourceId}`);
  }
}

// Export singleton instance
export const resourceService = new ResourceService();
